import { useEffect, useState } from 'react';

/**
 * useReducedMotion — Wraps `window.matchMedia('(prefers-reduced-motion: reduce)')`
 * in a React subscription so consumers stay in sync when the user flips the
 * OS-level "Reduce Motion" setting at runtime (Accessibility > Display in
 * macOS / iOS, Settings > Ease of Access > Display on Windows, GNOME
 * Universal Access).
 *
 * Returns `false` during SSR / when matchMedia isn't available so that the
 * default (motion ON) renders first paint and reduces only after mount — this
 * matches how `useLFO` and `useAudioReactive` already gate themselves.
 *
 * Centralizing the matchMedia lookup keeps the preference string in one
 * place and provides a stable reference for `useEffect` deps in consumers.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mql.matches);
    apply();
    // `addEventListener('change', …) is the modern API; current TS lib.dom
    // already types MediaQueryList.addListener as a deprecated-but-present
    // method, so no @ts-expect-error directive is needed.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
      return () => mql.removeEventListener('change', apply);
    }
    mql.addListener(apply);
    return () => mql.removeListener(apply);
  }, []);

  return reduced;
}
